import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  TablePagination,
  CircularProgress,
  Skeleton,
  Card,
  Grid,
  Stack,
  Divider,
  Chip,
} from "@mui/material";
import dayjs from "dayjs";
import { saveAs } from "file-saver";
import api from "../../utils/api";

const adminCardSx = {
  borderRadius: "18px",
  border: "1px solid rgba(47,99,173,0.08)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
  backgroundColor: "rgba(255,255,255,0.96)",
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
  },
};

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const BillingSummaryExport = ({ data, loading }) => {
  const [filters, setFilters] = useState({
    workOrderId: "",
    itemGroup: "",
    fromDate: "",
    toDate: "",
    association: "",
  });

  const [types, setTypes] = useState([]);
  const [subtypes, setSubtypes] = useState([]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [workOrderDetails, setWorkOrderDetails] = useState({});
  const [loadingDetails, setLoadingDetails] = useState({});

  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const [typeRes, subtypeRes] = await Promise.all([
          api.get("/workorder-types"),
          api.get("/workorder-subtypes"),
        ]);
        setTypes(typeRes.data || []);
        setSubtypes(subtypeRes.data || []);
      } catch (err) {
        console.error("Failed to fetch types/subtypes", err);
      }
    };
    fetchTypes();
  }, []);

  const getTypeName = (id) => types.find((t) => t.id === id)?.type || "—";
  const getSubtypeName = (id) =>
    subtypes.find((s) => s.id === id)?.subtype_name || "—";

  const filteredData = useMemo(() => {
    return (data || []).filter((item) => {
      const matchesWO =
        filters.workOrderId === "" ||
        `${item.work_order_id}`.includes(filters.workOrderId);

      const matchesGroup =
        filters.itemGroup === "" ||
        (item.item_group || "")
          .toLowerCase()
          .includes(filters.itemGroup.toLowerCase());

      const matchesAssoc =
        filters.association === "" ||
        (item.association_name || "")
          .toLowerCase()
          .includes(filters.association.toLowerCase());

      const sentAt = item.sent_at ? dayjs(item.sent_at) : null;

      const matchesFrom =
        !filters.fromDate ||
        (sentAt && sentAt.isAfter(dayjs(filters.fromDate).subtract(1, "day")));

      const matchesTo =
        !filters.toDate ||
        (sentAt && sentAt.isBefore(dayjs(filters.toDate).add(1, "day")));

      return (
        matchesWO && matchesGroup && matchesAssoc && matchesFrom && matchesTo
      );
    });
  }, [data, filters]);

  const groupedByWO = useMemo(
    () => [...new Set(filteredData.map((d) => d.work_order_id))],
    [filteredData]
  );

  const paginatedWO = useMemo(
    () =>
      groupedByWO.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [groupedByWO, page, rowsPerPage]
  );

  useEffect(() => {
    const fetchWorkOrderDetails = async () => {
      const uniqueIds = [...new Set(filteredData.map((d) => d.work_order_id))];
      if (uniqueIds.length === 0) return;

      const idsToFetch = uniqueIds.filter(
        (id) => !workOrderDetails[id] && !loadingDetails[id]
      );
      if (idsToFetch.length === 0) return;

      setLoadingDetails((prev) => {
        const next = { ...prev };
        idsToFetch.forEach((id) => {
          next[id] = true;
        });
        return next;
      });

      const detailsMap = {};

      await Promise.all(
        idsToFetch.map(async (id) => {
          try {
            const res = await api.get(`/getWorkOrderReportbilling/${id}`);
            detailsMap[id] = res.data;
          } catch (err) {
            console.error(`Failed to fetch details for Work Order ${id}`, err);
          } finally {
            setLoadingDetails((prev) => ({ ...prev, [id]: false }));
          }
        })
      );

      setWorkOrderDetails((prev) => ({ ...prev, ...detailsMap }));
    };

    fetchWorkOrderDetails();
  }, [filteredData, workOrderDetails, loadingDetails]);

  const handleExportCSV = () => {
    const headers = [
      "Work Order ID",
      "Association",
      "Member Address",
      "Item Group",
      "Description",
      "Details",
      "Price",
      "Sent At",
      "Transferred",
      "Resolved At",
      "Type",
      "Subtype",
    ];

    const rows = filteredData.map((item) => {
      const wo = workOrderDetails[item.work_order_id];
      const detailsText = wo?.details || item.details || "—";
      const memberAddress = wo?.full_address || "—";

      return [
        item.work_order_id,
        item.association_name || "—",
        memberAddress,
        item.item_group || "—",
        item.export_description || "—",
        (detailsText || "—").replace(/\r?\n/g, " ").trim() || "—",
        item.price ? parseFloat(item.price).toFixed(2) : "0.00",
        item.sent_at ? dayjs(item.sent_at).format("YYYY-MM-DD") : "—",
        item.exported ? "Yes" : "No",
        item.work_order_resolved_at
          ? dayjs(item.work_order_resolved_at).format("YYYY-MM-DD")
          : "—",
        getTypeName(item.workorder_type_id),
        getSubtypeName(item.workorder_subtype_id),
      ];
    });

    const csvContent = [headers, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const BOM = "\uFEFF";
    const blob = new Blob([BOM + csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    saveAs(blob, `billing_export_${dayjs().format("YYYYMMDD_HHmmss")}.csv`);
  };

  const handleResetFilters = () => {
    setFilters({
      workOrderId: "",
      itemGroup: "",
      fromDate: "",
      toDate: "",
      association: "",
    });
    setPage(0);
  };

  if (loading) {
    return (
      <Card sx={{ ...adminCardSx, p: 3 }}>
        <Typography sx={{ color: "#6b778c" }}>Loading report...</Typography>
      </Card>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h5"
          sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}
        >
          Billed Work Orders Report
        </Typography>
        <Typography variant="body2" sx={{ color: "#6b778c" }}>
          Review billed work orders, inspect exported line items, and export the
          filtered results to CSV.
        </Typography>
      </Box>

      <Card
        elevation={0}
        sx={{
          ...adminCardSx,
          p: { xs: 2, md: 3 },
          mb: 3,
        }}
      >
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}
          >
            Filters
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b778c" }}>
            Filter by work order, item group, association, and date range.
          </Typography>
        </Box>

        <Grid container spacing={2.5} alignItems="center">
          <Grid item xs={12} sm={6} md={2.4}>
            <TextField
              fullWidth
              label="Work Order ID"
              size="small"
              value={filters.workOrderId}
              onChange={(e) =>
                setFilters({ ...filters, workOrderId: e.target.value })
              }
              sx={fieldSx}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <TextField
              fullWidth
              label="Item Group"
              size="small"
              value={filters.itemGroup}
              onChange={(e) =>
                setFilters({ ...filters, itemGroup: e.target.value })
              }
              sx={fieldSx}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <TextField
              fullWidth
              label="Association"
              size="small"
              value={filters.association}
              onChange={(e) =>
                setFilters({ ...filters, association: e.target.value })
              }
              sx={fieldSx}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <TextField
              fullWidth
              label="From Date"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={filters.fromDate}
              onChange={(e) =>
                setFilters({ ...filters, fromDate: e.target.value })
              }
              sx={fieldSx}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <TextField
              fullWidth
              label="To Date"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={filters.toDate}
              onChange={(e) =>
                setFilters({ ...filters, toDate: e.target.value })
              }
              sx={fieldSx}
            />
          </Grid>

          <Grid item xs={12}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              justifyContent="flex-end"
            >
              <Button
                variant="outlined"
                onClick={handleResetFilters}
                sx={{
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 700,
                }}
              >
                Reset Filters
              </Button>

              <Button
                variant="contained"
                onClick={handleExportCSV}
                sx={{
                  backgroundColor: "#2f63ad",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 700,
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#275592",
                    boxShadow: "0 8px 20px rgba(47,99,173,0.22)",
                  },
                }}
              >
                Export CSV
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Card>

      <Box sx={{ mb: 2 }}>
        <Chip
          label={`${groupedByWO.length} work order${
            groupedByWO.length !== 1 ? "s" : ""
          } found`}
          sx={{
            borderRadius: "999px",
            fontWeight: 700,
            backgroundColor: "rgba(47,99,173,0.08)",
            color: "#2f63ad",
          }}
        />
      </Box>

      {groupedByWO.length === 0 && (
        <Card sx={{ ...adminCardSx, p: 3 }}>
          <Typography sx={{ color: "#6b778c" }}>
            No results found for the selected filters.
          </Typography>
        </Card>
      )}

      {paginatedWO.map((woId) => {
        const group = filteredData.filter((d) => d.work_order_id === woId);
        const assocName = group[0]?.association_name || "—";
        const woTotal = group[0]?.work_order_total || 0;
        const resolved = group[0]?.work_order_resolved_at;
        const woType = group[0]?.workorder_type_id;
        const woSubType = group[0]?.workorder_subtype_id;

        const details = workOrderDetails[woId];
        const isDetailsLoading = !!loadingDetails[woId] && !details;

        const createdAt = details?.created_at
          ? dayjs(details.created_at).format("YYYY-MM-DD")
          : "—";

        const openedBy = details?.opened_by_name || "—";
        const priority = details?.priority || "—";
        const assignedTo = details?.assign_to || "—";
        const memberAddress = details?.full_address || "—";

        return (
          <Card
            key={woId}
            elevation={0}
            sx={{
              ...adminCardSx,
              p: { xs: 2, md: 3 },
              mb: 3,
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
              spacing={1.5}
              sx={{ mb: 1.5 }}
            >
              <Box>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: "#223142" }}
                  >
                    Work Order #{woId}
                  </Typography>
                  {isDetailsLoading && <CircularProgress size={16} />}
                </Stack>

                <Typography
                  variant="body2"
                  sx={{ color: "#6b778c", mt: 0.5 }}
                >
                  {assocName}
                </Typography>
              </Box>

              <Chip
                label={`Total ${formatCurrency(woTotal)}`}
                sx={{
                  borderRadius: "999px",
                  fontWeight: 700,
                  backgroundColor: "rgba(34,197,94,0.10)",
                  color: "#15803d",
                }}
              />
            </Stack>

            <Divider sx={{ mb: 2 }} />

            {isDetailsLoading ? (
              <>
                <Skeleton variant="text" width={280} height={24} />
                <Skeleton variant="text" width="90%" height={22} />
              </>
            ) : (
              <>
                <Typography
                  variant="body2"
                  sx={{ color: "#6b778c", mb: 1 }}
                >
                  Address: {memberAddress}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{ color: "#6b778c", mb: 2 }}
                >
                  Type: {getTypeName(woType)} | Subtype:{" "}
                  {getSubtypeName(woSubType)} | Created: {createdAt} | Resolved:{" "}
                  {resolved ? dayjs(resolved).format("YYYY-MM-DD") : "—"} |
                  Priority: {priority} | Assigned To: {assignedTo} | Opened By:{" "}
                  {openedBy}
                </Typography>
              </>
            )}

            {isDetailsLoading ? (
              <Box
                sx={{
                  backgroundColor: "#fff",
                  borderRadius: "14px",
                  border: "1px solid rgba(47,99,173,0.08)",
                  p: 2,
                  mb: 2,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, mb: 0.75, color: "#223142" }}
                >
                  Details
                </Typography>
                <Skeleton variant="text" width="95%" />
                <Skeleton variant="text" width="90%" />
                <Skeleton variant="text" width="85%" />
              </Box>
            ) : (
              details?.details && (
                <Box
                  sx={{
                    backgroundColor: "#fff",
                    borderRadius: "14px",
                    border: "1px solid rgba(47,99,173,0.08)",
                    p: 2,
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, mb: 0.75, color: "#223142" }}
                  >
                    Details
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: "pre-line", color: "#445166" }}
                  >
                    {details.details}
                  </Typography>
                </Box>
              )
            )}

            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                borderRadius: "14px",
                overflow: "hidden",
                border: "1px solid rgba(47,99,173,0.08)",
              }}
            >
              <Table size="small">
                <TableHead
                  sx={{
                    backgroundColor: "rgba(47,99,173,0.04)",
                  }}
                >
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, color: "#223142" }}>
                      Item Group
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: "#223142" }}>
                      Description
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: "#223142" }}>
                      Price
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: "#223142" }}>
                      Sent At
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: "#223142" }}>
                      Transferred to Jonas
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {group.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>{item.item_group || "—"}</TableCell>
                      <TableCell>{item.export_description || "—"}</TableCell>
                      <TableCell>{formatCurrency(item.price)}</TableCell>
                      <TableCell>
                        {item.sent_at
                          ? dayjs(item.sent_at).format("YYYY-MM-DD")
                          : "—"}
                      </TableCell>
                      <TableCell>{item.exported ? "Yes" : "No"}</TableCell>
                    </TableRow>
                  ))}

                  <TableRow
                    sx={{
                      backgroundColor: "rgba(47,99,173,0.04)",
                    }}
                  >
                    <TableCell
                      colSpan={2}
                      sx={{ fontWeight: 700, color: "#223142" }}
                    >
                      Work Order Total
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: "#223142" }}>
                      {formatCurrency(woTotal)}
                    </TableCell>
                    <TableCell colSpan={2}></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        );
      })}

      {groupedByWO.length > 0 && (
        <Card sx={{ ...adminCardSx, mt: 2 }}>
          <TablePagination
            component="div"
            count={groupedByWO.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage="Work Orders per page:"
          />
        </Card>
      )}
    </Box>
  );
};

export default BillingSummaryExport;